/**
 * WHY A COMPANY WAS NOT CREATED — in Hebrew, with the next step and a fault reference.
 * ===================================================================================
 *
 * THE OWNER'S FINDING 6, 2026-10-20: *"בכרטיס הלקוח … לא מצאנו מסלול ברור להשלמת ההגדרות"*, and
 * underneath it a company-creation path that accepted an empty official number, discarded the one
 * it was given, and recorded a status the database rejects. The server now refuses by name; this is
 * where those names become sentences.
 *
 * The vocabulary is the company-management chapter's (0.6 §4, §14), which makes five fields
 * mandatory to activate a company — entity type, official number, user, e-mail, accounting system —
 * and rules out the alternative explicitly: *"אין מסלול ״חברת טיוטה ללא מספר רשמי״ כחלופה להשלמת
 * ההגדרות"*.
 *
 * ── WHAT IS NOT HERE, AND IT IS NOT AN OVERSIGHT ────────────────────────────────────────────────
 *
 * No client-side validation of the number. The check digit is verified in ONE place — the server,
 * against the format `core.external_system` declares — and a second implementation in the browser
 * is how the two come to disagree about a nine-digit number. The cost is one round trip; the
 * alternative is two rules.
 */

import type { ConflictBody } from './client';

export interface CompanyRefusalText {
  readonly titleHe: string;
  readonly explanationHe: string;
  /** What is NOT in the database because of this refusal. */
  readonly unchangedHe: string;
  readonly nextStepHe: string;
}

const REFUSAL_HE: Readonly<Record<string, CompanyRefusalText>> = {
  /*
   * ── THE ACCOUNTING-SYSTEM SELECTION. Owner finding 6, closed 2026-10-21. ──────────────────────
   *
   * Every one of these says the same two things, because they are the two an operator needs: WHAT
   * WAS NOT WRITTEN, and WHAT TO DO. None of them mentions a connection, in either direction --
   * *"בלי מזהה ספק מומצא ובלי להציג ״מחובר״ לפני בדיקה"* -- so a refused selection cannot be read
   * as a failed connection attempt, which is a thing that never happened.
   */
  system_code_required: {
    titleHe: 'לא נבחרה מערכת',
    explanationHe:
      'כדי לשמור יש לבחור מערכת מהרשימה. הרשימה נקראת מהקטלוג של ההתקנה, ואין להקליד שם או קוד ' +
      'שאינו בה — בחירה חופשית הייתה יוצרת מזהה ספק שלא קיים.',
    unchangedHe: 'הבחירה הקיימת של החברה לא שונתה.',
    nextStepHe: 'בחירת מערכת מהרשימה ושמירה.',
  },
  system_unknown: {
    titleHe: 'המערכת שנבחרה אינה בקטלוג',
    explanationHe:
      'הקוד שהגיע לשרת אינו של מערכת שקיימת בקטלוג ההתקנה. בדרך כלל זה אומר שהרשימה שבמסך נקראה ' +
      'לפני שינוי בקטלוג.',
    unchangedHe: 'הבחירה הקיימת של החברה לא שונתה.',
    nextStepHe: 'רענון המסך, כדי לקרוא את הרשימה מחדש, ובחירה שוב.',
  },
  system_not_selectable: {
    titleHe: 'המערכת אינה ניתנת לבחירה',
    explanationHe:
      'המערכת קיימת בקטלוג אך אינה מוצעת לבחירה כמערכת הנהלת חשבונות: היא הוצאה משירות, או שהיא ' +
      'מסווגת כספק בנקאות או כמקור אחר. הרשימה במסך מציגה מערכות הנהלת חשבונות פעילות בלבד.',
    unchangedHe: 'הבחירה הקיימת של החברה לא שונתה.',
    nextStepHe: 'בחירת מערכת אחרת מהרשימה. אם המערכת הנדרשת אינה שם, סיווגה בקטלוג הוא הצעד שלפני כן.',
  },
  official_id_required: {
    titleHe: 'חסר המספר הרשמי של החברה',
    explanationHe:
      'מספר רשמי הוא שדה חובה להקמת חברה: ח״פ לחברה בע״מ, מספר עוסק לעוסק מורשה או פטור, ' +
      'תעודת זהות ליחיד. אין מסלול של חברת טיוטה ללא מספר, ואין להזין מספר דמה כדי להמשיך.',
    unchangedHe: 'לא נוצרה חברה ולא נשמר דבר.',
    nextStepHe: 'הזנת המספר הרשמי כפי שהוא מופיע במסמכי החברה, ושמירה מחדש.',
  },
  official_id_malformed: {
    titleHe: 'המספר הרשמי אינו בתבנית הנדרשת',
    explanationHe:
      'המספר צריך להיות תשע ספרות. רווחים, מקפים וגרש מוסרים אוטומטית, אבל אות או ספרה חסרה ' +
      'אינן מתוקנות — המערכת אינה משנה מספר שהוזן.',
    unchangedHe: 'לא נוצרה חברה.',
    nextStepHe: 'בדיקת המספר במסמך המקורי והזנתו מחדש, תשע ספרות.',
  },
  official_id_check_digit_failed: {
    titleHe: 'ספרת הביקורת של המספר אינה מתאימה',
    explanationHe:
      'המספר הוא תשע ספרות, וספרת הביקורת אינה מתאימה לשמונה הראשונות. זה מה שתופס ספרה שהוחלפה ' +
      'או שתי ספרות שהתהפכו בהקלדה — שגיאה שבדיקת אורך לבדה אינה רואה.',
    unchangedHe: 'לא נוצרה חברה.',
    nextStepHe: 'השוואת המספר למסמך הרשמי, ספרה אחר ספרה, והזנתו מחדש.',
  },
  entity_kind_unknown: {
    titleHe: 'סוג הישות אינו מוכר',
    explanationHe: 'הגיע לשרת סוג ישות שאינו אחד מאלה שהמערכת מכירה, ולכן לא ניתן לדעת איזה מספר רשמי נדרש.',
    unchangedHe: 'לא נוצרה חברה.',
    nextStepHe: 'בחירת סוג הישות מהרשימה ושמירה מחדש.',
  },
  identifier_namespace_not_configured: {
    titleHe: 'לא הוגדר במערכת מרחב מזהים רשמי',
    explanationHe:
      'כדי לבדוק מספר רשמי נדרשת הגדרה של רשות המזהים ושל תבנית המספר שלה. בהתקנה הזאת היא חסרה, ' +
      'והמערכת אינה ממציאה תבנית במקומה.',
    unchangedHe: 'לא נוצרה חברה.',
    nextStepHe: 'העברת האסמכתה שלמטה לבדיקה. זו תקלת הגדרה בהתקנה, לא טעות בהזנה.',
  },
  limit_reached: {
    titleHe: 'מכסת החברות של הארגון מלאה',
    explanationHe:
      'מספר החברות שהוקמו הגיע למכסה שנקבעה למנוי. כל מה שקיים ממשיך לעבוד; לא ניתן להוסיף חברה נוספת.',
    unchangedHe: 'לא נוצרה חברה.',
    nextStepHe: 'הגדלת המכסה במנוי, או סגירת חברה שאינה בשימוש, לפני הקמה נוספת.',
  },
};

const GENERIC: CompanyRefusalText = {
  titleHe: 'החברה לא הוקמה',
  explanationHe:
    'השרת סירב לבקשה. הסירוב נעשה לפני הכתיבה, ולכן לא נוצרה חברה — אבל לגרסה הזאת אין נוסח ' +
    'בעברית לסיבה שהשרת נתן.',
  unchangedHe: 'לא נוצרה חברה.',
  nextStepHe: 'העברת האסמכתה שלמטה לבדיקה.',
};

/** Exposed for the mirror test only: every reason this build has Hebrew for. */
export function mappedCompanyRefusals(): readonly string[] {
  return Object.keys(REFUSAL_HE);
}

export function companyRefusalText(body: ConflictBody): CompanyRefusalText {
  const base = REFUSAL_HE[body.error] ?? GENERIC;
  if (body.error !== 'limit_reached' || body.limit === undefined) return base;
  // THE COUNT AND THE LIMIT, which 08 §7 requires of a capacity refusal: *"never a generic 403"*.
  return {
    ...base,
    explanationHe:
      `${String(body.current ?? 0)} חברות מתוך מכסה של ${String(body.limit)}. ` + base.explanationHe,
  };
}
